import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, CheckCircle2, AlertTriangle, RefreshCw, Send, Copy } from "lucide-react";
import { toast } from "sonner";

/**
 * EmailInboundStatusPanel — Settings → Email → Inbound diagnostics.
 * One-click check of DNS, Resend webhook config, env vars, and the last
 * inbound email received. Plus a test-send button that drops a message in
 * the admin's inbox with Reply-To set to this tenant's inbound address.
 */
export default function EmailInboundStatusPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/emails/inbound-diagnostics");
      setData(data);
      if (!testTo) setTestTo((data.last_inbound_from) || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Diagnostics failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    if (!testTo.trim()) { toast.error("Enter a recipient email"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/emails/inbound-diagnostics/send-test", { to_email: testTo.trim() });
      if (data.ok) {
        toast.success(`Test sent — Reply-To ${data.reply_to}`);
      } else {
        toast.error("Email provider rejected the send. Check env vars.");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const copy = async (txt) => {
    try { await navigator.clipboard.writeText(txt); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  if (loading) return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp p-6 mb-8">
      <div className="text-sm text-zinc-500">Loading email inbound diagnostics…</div>
    </div>
  );
  if (!data) return null;

  const mxOk = data.mx_ok;
  const envMissing = Object.entries(data.env_status || {}).filter(([, v]) => !v).map(([k]) => k);

  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp overflow-hidden mb-8" data-testid="email-inbound-panel">
      <div className="p-6 border-b border-zinc-100 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-sky-50 flex items-center justify-center">
            <Mail className="w-5 h-5 text-sky-600"/>
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold">Email inbound diagnostics</h2>
            <p className="text-sm text-zinc-500">Verify DNS, webhook, and env configuration for replies to the right record.</p>
          </div>
        </div>
        <Button variant="outline" onClick={load} data-testid="email-inbound-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1"/>Refresh</Button>
      </div>

      <div className="p-6 space-y-4">
        {/* Reply address */}
        <section>
          <div className="text-[10px] font-mono-accent uppercase tracking-widest text-zinc-500 mb-1">Email provider</div>
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span className={`text-xs font-mono-accent uppercase tracking-wider px-2 py-1 rounded ${data.provider === "own_resend" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid="email-inbound-provider">
              {data.provider === "own_resend" ? "Own Resend account" : "Emergent-managed (fallback)"}
            </span>
            <span className={`text-xs font-mono-accent uppercase tracking-wider px-2 py-1 rounded ${data.webhook_signature === "enforced" ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-600"}`} data-testid="email-inbound-sig">
              Webhook signature: {data.webhook_signature}
            </span>
          </div>
          {data.sample_from && (
            <div className="mb-3 text-xs text-zinc-600">
              <span className="text-zinc-500">Customers see From: </span>
              <code className="bg-zinc-100 px-2 py-0.5 rounded font-mono-accent">{data.sample_from}</code>
            </div>
          )}
          <div className="text-[10px] font-mono-accent uppercase tracking-widest text-zinc-500 mb-1">Your inbound reply address</div>
          <div className="flex items-center gap-2 flex-wrap">
            <code className="text-xs bg-zinc-100 px-2 py-1 rounded font-mono-accent" data-testid="email-inbound-reply-to">{data.sample_reply_to}</code>
            <button onClick={() => copy(data.sample_reply_to)} className="text-xs text-zinc-500 hover:text-[#FF5A00] inline-flex items-center gap-1"><Copy className="w-3 h-3"/>Copy</button>
          </div>
          <p className="text-[11px] text-zinc-500 mt-1">
            Every customer-facing email LeadForge sends on your behalf is tagged with this address as the Reply-To so replies land back here, not at the platform sender.
          </p>
        </section>

        {/* MX / Resend status */}
        <section className="grid md:grid-cols-2 gap-4">
          <div className={`rounded-md border p-3 ${mxOk ? "border-emerald-200 bg-emerald-50/40" : "border-rose-200 bg-rose-50/40"}`} data-testid="email-inbound-mx-status">
            <div className="flex items-center gap-2 mb-1">
              {mxOk ? <CheckCircle2 className="w-4 h-4 text-emerald-600"/> : <AlertTriangle className="w-4 h-4 text-rose-600"/>}
              <span className="font-medium text-sm">MX record on {data.inbound_domain}</span>
            </div>
            {(data.mx_records || []).length === 0 && (
              <div className="text-xs text-rose-700">No MX found — DNS is not configured yet.</div>
            )}
            {(data.mx_records || []).map((m, i) => (
              <div key={i} className="text-xs font-mono-accent text-zinc-700">
                {m.preference !== null ? `${m.preference} ` : ""}{m.exchange}
              </div>
            ))}
          </div>
          <div className={`rounded-md border p-3 ${envMissing.length === 0 ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/40"}`}>
            <div className="flex items-center gap-2 mb-1">
              {envMissing.length === 0 ? <CheckCircle2 className="w-4 h-4 text-emerald-600"/> : <AlertTriangle className="w-4 h-4 text-amber-600"/>}
              <span className="font-medium text-sm">Backend env vars</span>
            </div>
            <ul className="text-xs text-zinc-700 space-y-0.5">
              {Object.entries(data.env_status || {}).map(([k, v]) => (
                <li key={k} className="font-mono-accent">
                  <span className={v ? "text-emerald-700" : "text-rose-700"}>{v ? "✓" : "✗"}</span> {k}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* DNS instructions */}
        <section className="rounded-md border border-zinc-200 bg-zinc-50/60 p-4" data-testid="email-inbound-dns-help">
          <div className="text-xs font-mono-accent uppercase tracking-widest text-zinc-500 mb-2">1. Add this DNS record</div>
          <table className="text-xs w-full">
            <tbody>
              <tr className="border-b border-zinc-200"><td className="py-1 pr-4 text-zinc-500">Host</td><td className="font-mono-accent">{data.dns_instructions.host}</td></tr>
              <tr className="border-b border-zinc-200"><td className="py-1 pr-4 text-zinc-500">Type</td><td className="font-mono-accent">{data.dns_instructions.type}</td></tr>
              <tr className="border-b border-zinc-200"><td className="py-1 pr-4 text-zinc-500">Priority</td><td className="font-mono-accent">{data.dns_instructions.priority}</td></tr>
              <tr><td className="py-1 pr-4 text-zinc-500">Value</td><td className="font-mono-accent">{data.dns_instructions.value}</td></tr>
            </tbody>
          </table>
          <p className="text-[11px] text-zinc-500 mt-3">{data.dns_instructions.notes}</p>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-zinc-500 mt-4 mb-1">2. Resend webhook URL</div>
          <div className="flex items-center gap-2 flex-wrap">
            <code className="text-xs bg-white border border-zinc-200 px-2 py-1 rounded font-mono-accent">{data.expected_webhook || "<APP_PUBLIC_URL not set>"}</code>
            {data.expected_webhook && (
              <button onClick={() => copy(data.expected_webhook)} className="text-xs text-zinc-500 hover:text-[#FF5A00] inline-flex items-center gap-1"><Copy className="w-3 h-3"/>Copy</button>
            )}
          </div>
          <p className="text-[11px] text-zinc-500 mt-1">In the Resend dashboard: <strong>Domains → Add domain → reply.iqleadforge.com → enable Inbound</strong>, then point the inbound webhook to the URL above. Resend auto-forwards every address on this subdomain to that one URL — LeadForge routes by local-part.</p>
        </section>

        {/* Last inbound */}
        <section className="rounded-md border border-zinc-200 p-3">
          <div className="text-[10px] font-mono-accent uppercase tracking-widest text-zinc-500 mb-1">Last inbound email</div>
          {data.last_inbound_at ? (
            <div className="text-xs" data-testid="email-inbound-last">
              <div><strong>From:</strong> {data.last_inbound_from}</div>
              <div><strong>Subject:</strong> {data.last_inbound_subject}</div>
              <div className="text-zinc-500">at {new Date(data.last_inbound_at).toLocaleString()}</div>
              <div className="text-zinc-500 mt-1">Total received: {data.inbound_messages_total}</div>
            </div>
          ) : (
            <div className="text-xs text-zinc-500">No inbound emails received yet.</div>
          )}
        </section>

        {/* Test send */}
        <section className="rounded-md border border-zinc-200 p-4" data-testid="email-inbound-test-send">
          <div className="text-xs font-mono-accent uppercase tracking-widest text-zinc-500 mb-2">Send test + check inbound</div>
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              type="email"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="your@email.com"
              className="h-9 max-w-sm"
              data-testid="email-inbound-test-to"
            />
            <Button onClick={sendTest} disabled={sending} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="email-inbound-send-test-btn">
              <Send className="w-3.5 h-3.5 mr-1"/>{sending ? "Sending…" : "Send test"}
            </Button>
          </div>
          <p className="text-[11px] text-zinc-500 mt-1">We'll email you with the Reply-To set to <code className="font-mono-accent">{data.sample_reply_to}</code>. Open the message, hit Reply, send any text — it should appear here (and in Unread Communications) within seconds once DNS + webhook are live.</p>
        </section>
      </div>
    </div>
  );
}
